
import { defineComponent, computed, reactive, ref, watch } from 'vue';

import WdwDynbox from '@/components/general/WdwDynbox.vue';
import QuestionSuggestions from '@/components/askus/QuestionSuggestions.vue';
import FormControlInput from '@/components/general/FormControl/Input.vue';
import FormControlTextarea from '@/components/general/FormControl/TextArea.vue';
import FormControlAttributes from '@/components/general/FormControl/Attributes.vue';

import { Form } from 'vee-validate';
import { IAskusQuestion } from '@/models/askus.model';

import { askusStore } from '@/store/askus-store';
import askusRepository from '@/api/askusRepository';

import useConverter from '@/composables/general/useConverter';
import useStringManip from '@/composables/general/useStringManip';
import useFormInputDependency from '@/composables/general/useFormInputDependency';

export default defineComponent({
  name: 'QuestionForm',
  props: {},
  components: {
    WdwDynbox,
    QuestionSuggestions,
    FormControlAttributes,
    FormControlTextarea,
    FormControlInput,
    Form,
  },
  emits: ['closeForm', 'openQuestion'],
  setup(props, context) {
    const { truncateAfterWord } = useStringManip();
    const { formattedDate, toBool } = useConverter();
    const { evaluateFormIfCode } = useFormInputDependency();

    let formValues: any = {};
    const suggestionsText = ref('');

    const data: any = reactive({
      // Flags for loader
      loader: {
        fetching_question: true,
        submitting_question: false,
      },
      // Server-related errors
      errors: {},
      // Evaluation of visible_if_code and visible_if_value
      visibleIfResult: {},
      // Evaluation of required_if_code and required_if_value
      requiredIfResult: {},
      // Success message
      success: '',
      // question result after edited / created
      result_question: null,
    });

    const askus = computed(() => {
      return askusStore.getState().askus;
    });
    const askusAttributes = computed(() => {
      return askus.value.attributes;
    });

    // Init requiredIfResults
    if (askusAttributes.value && askusAttributes.value.size > 0) {
      askusAttributes.value.forEach((attr: any) => {
        data.requiredIfResult[attr.code] = toBool(attr.required);
      });
    }

    const displayServerErrors = (error: any) => {
      try {
        const errData = error.response.data;
        data.errors['all'] = errData.message;
        // If there are attribute errors, we try to display the attribute's default error message
        // Although this is rarely executed since attribute checking is also done before submitting
        if (errData.attribute_ids) {
          const attributes = askusAttributes.value;
          errData.attribute_ids.forEach((id: number) => {
            const existing = attributes.filter((attr: any) => attr.id == id);
            if (existing.length > 0) {
              data.errors['attribute-' + id] = existing[0].error;
            }
          });
        }
      } catch (e) {
        data.errors['all'] = 'Fehler: ' + e;
      }
    };

    function setSuggestionsText(text: any) {
      suggestionsText.value = text;
    }

    function openQuestion(question: IAskusQuestion, questionid: number) {
      context.emit('openQuestion', question, questionid);
    }
    function cancel() {
      context.emit('closeForm');
    }

    function resetForm(formAction: any) {
      // Reset the form
      formAction.resetForm();
      formValues = {};
    }

    function submitQuestion(values: any, actions: any) {
      // we can use values for the normal and attribute fields
      // but we need to use uploadedFiles to gain access to the picked files
      data.loader.submitting_question = true;
      data.errors = {};
      data.success = '';

      // Prepare data and file
      const askusid = askus.value.id;

      values.askus_id = askusid;

      // Post new thought
      askusRepository
        .createQuestion(askusid, values)
        .then((response: any) => {
          const respData = response.data.data;
          if (respData) {
            data.result_question = respData;
          }
          // Reset the form
          resetForm(actions);
          data.loader.submitting_question = false;

          // emit success
          context.emit('closeForm', { showSuccess: true });
        })
        .catch(error => {
          displayServerErrors(error);
          data.loader.submitting_question = false;
        });
    }

    function onValidatedFormField(fieldValue: any) {
      formValues[fieldValue.name] = fieldValue;
      // Evaluate the whole attribute fields based on its visible_if_code
      data.visibleIfResult = evaluateFormIfCode(
        'visible_if_code',
        'visible_if_value',
        data.visibleIfResult,
        askusAttributes.value,
        fieldValue.code,
        fieldValue.value,
        fieldValue.type
      );
      // Evaluate the whole attribute fields based on its required_if_code
      data.requiredIfResult = evaluateFormIfCode(
        'required_if_code',
        'required_if_value',
        data.requiredIfResult,
        askusAttributes.value,
        fieldValue.code,
        fieldValue.value,
        fieldValue.type
      );
    }
    return {
      askus,
      data,
      suggestionsText,
      truncateAfterWord,
      formattedDate,
      setSuggestionsText,
      submitQuestion,
      openQuestion,
      cancel,
      onValidatedFormField,
    };
  },
});
